// File: src/pages/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { io } from "socket.io-client"; // 1. Import Socket Client
import CountUp from 'react-countup';    // 2. Import Hiệu ứng số chạy

// Component Card được nâng cấp với CountUp
const StatCard = ({ title, value, icon, color, isCurrency }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:scale-105">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${color} shadow-lg`}>
            <span className="material-symbols-outlined text-2xl">{icon}</span>
        </div>
        <div>
            <p className="text-gray-500 text-sm font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-800">
                {/* 3. Dùng CountUp thay vì hiển thị số tĩnh */}
                <CountUp 
                    end={value} 
                    duration={2} // Chạy trong 2 giây
                    separator="." // Dấu phân cách hàng nghìn
                    suffix={isCurrency ? 'đ' : ''} // Đuôi tiền tệ
                />
            </h3>
        </div>
    </div>
);

export default function AdminDashboard() {
    const [stats, setStats] = useState({ users: { candidate: 0, business: 0 }, jobs: 0, revenue: 0 });
    const [chartData, setChartData] = useState([]); // Tách state chartData riêng để dễ quản lý
    const [timeRange, setTimeRange] = useState('day');
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    const fetchStats = async () => {
        const token = localStorage.getItem('skillmatch_token');
        try {
            const res = await fetch(`${API_URL}/api/admin/stats?timeRange=${timeRange}`, {
                headers: { 'x-auth-token': token }
            });
            const data = await res.json();
            
            // Cập nhật state (Cấu trúc data backend trả về: { overview, chartData })
            if (data.overview) {
                setStats({
                    users: { 
                        candidate: data.overview.candidates || 0, // Lấy từ 'candidates'
                        business: data.overview.businesses || 0   // Lấy từ 'businesses'
                    },
                    jobs: data.overview.jobs || 0,
                    revenue: data.overview.revenue || 0
                });
            }
            if (data.chartData) setChartData(data.chartData);

        } catch (e) { console.error(e); }
    };

    // 4. Effect lắng nghe Socket Real-time
    useEffect(() => {
        fetchStats(); // Gọi lần đầu khi vào trang

        // Kết nối Socket
        const socket = io(API_URL);

        // Lắng nghe sự kiện 'server:update_stats' từ Backend
        socket.on('server:update_stats', () => {
            console.log("🔔 Nhận tín hiệu update từ server -> Refresh data!");
            fetchStats(); // Tự động gọi lại API để lấy số mới
        });

        // Cleanup khi thoát trang
        return () => {
            socket.disconnect();
        };
    }, [timeRange]); // Thêm dependency timeRange để khi đổi filter chart cũng cập nhật

    const formatCurrency = (amount) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Dashboard Quản Trị</h2>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {/* Truyền prop isCurrency cho ô Doanh thu */}
                <StatCard title="Ứng viên" value={stats.users?.candidate || 0} icon="person" color="bg-blue-500" />
                <StatCard title="Doanh nghiệp" value={stats.users?.business || 0} icon="domain" color="bg-indigo-500" />
                <StatCard title="Tin tuyển dụng" value={stats.jobs || 0} icon="work" color="bg-green-500" />
                <StatCard title="Doanh thu thực tế" value={stats.revenue || 0} icon="payments" color="bg-yellow-500" isCurrency={true} />
            </div>

            {/* Biểu đồ doanh thu */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600">monitoring</span>
                        Biểu đồ doanh thu
                    </h3>
                    
                    <div className="bg-gray-100 p-1 rounded-lg flex text-sm font-medium">
                        {['day', 'month', 'year'].map((range) => (
                            <button 
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 rounded-md transition-all ${timeRange === range ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {range === 'day' ? 'Theo Ngày' : (range === 'month' ? 'Theo Tháng' : 'Theo Năm')}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="h-[350px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                            <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} tickFormatter={(val) => `${val/1000}k`} />
                            <Tooltip 
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                formatter={(value) => [formatCurrency(value), "Doanh thu"]}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="revenue" 
                                stroke="#2563EB" 
                                strokeWidth={3}
                                fillOpacity={1} 
                                fill="url(#colorRevenue)" 
                                animationDuration={1500} // Animation cho biểu đồ
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}